import { Button, Checkbox, ChipsInput, IconButton, SlideModalBase } from "@components/library";
import { SearchBarInput } from "@components/library/Inputs/SearchBarInput";
import { COLORS, FEATURE_FLAGS } from "@constants";
import { useSearchParams } from "@hooks/router";
import { useIsMount } from "@hooks/useIsMount";
import { RootState } from "@redux/store";
import {
  REQUEST_PARTNER_TYPE_LABELS,
  REQUEST_USE_CASE_LABELS,
  RequestPartnerTypes,
  RequestUseCase,
} from "@tsTypes/requests";
import { PARTNERSHIP_MODEL_LABELS, RfpPartnershipModel } from "@tsTypes/rfps";
import { ChangeEvent, Dispatch, SetStateAction, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import styled from "styled-components";
import { SORT_BY_OPTIONS } from "./RespondToRequests";
import {
  CheckboxContainer,
  FilterContainer,
  FilterSection,
  ResetFiltersButton,
} from "./sharedFilterStyles";

interface Props {
  searchFilter: string[];
  setSearchFilter: Dispatch<SetStateAction<string[]>>;
  useCaseFilter: RequestUseCase[];
  setUseCaseFilter: Dispatch<SetStateAction<RequestUseCase[]>>;
  partnershipModelsFilter: RfpPartnershipModel[];
  setPartnershipModelsFilter: Dispatch<SetStateAction<RfpPartnershipModel[]>>;
  partnerTypesFilter: RequestPartnerTypes[];
  setPartnerTypesFilter: Dispatch<SetStateAction<RequestPartnerTypes[]>>;
  sortBy: (typeof SORT_BY_OPTIONS)[number];
  setSortBy: Dispatch<SetStateAction<(typeof SORT_BY_OPTIONS)[number]>>;
  totalCount: number;
  setCurrentPageNum: (newPageNum: number) => void;
}

const SEARCH_FILTER_PARAM = "search";
const USE_CASE_FILTER_PARAM = "use_case";
const PARTNERSHIP_MODELS_FILTER_PARAM = "partnership_models";
const PARTNER_TYPES_FILTER_PARAM = "partner_types";

// Listened in the order for the marketplace filters
const USE_CASE_OPTIONS = [
  RequestUseCase.NOVEL_TECHNOLOGY,
  RequestUseCase.PROCESS_PRODUCT_IMPROVEMENTS,
  RequestUseCase.TESTING_ANALYSIS,
  RequestUseCase.EXPERTS,
  // TODO (Andrew): Temporarily hiding until RFSs are moved to the main marketplace tab
  // RequestUseCase.PROGRAM_PARTICIPANTS,
];

const PARTNERSHIP_MODEL_OPTIONS = [
  RfpPartnershipModel.SPONSORED_RESEARCH,
  RfpPartnershipModel.GIFT,
  RfpPartnershipModel.CO_DEVELOPMENT,
  RfpPartnershipModel.SUPPLY_PURCHASE,
  RfpPartnershipModel.LICENSING,
  RfpPartnershipModel.FEE_FOR_SERVICE,
  RfpPartnershipModel.EQUITY_INVESTMENT,
  RfpPartnershipModel.EMPLOYMENT,
];

const PARTNER_TYPE_OPTIONS = [
  RequestPartnerTypes.ACADEMIC_RESEARCHERS,
  RequestPartnerTypes.STARTUPS,
  RequestPartnerTypes.SUPPLIERS,
  RequestPartnerTypes.SERVICE_PROVIDERS,
  RequestPartnerTypes.CONSULTANTS,
];

export default function RespondToRequestsFilters({
  searchFilter,
  setSearchFilter,
  useCaseFilter,
  setUseCaseFilter,
  partnershipModelsFilter,
  setPartnershipModelsFilter,
  partnerTypesFilter,
  setPartnerTypesFilter,
  sortBy,
  setSortBy,
  totalCount,
  setCurrentPageNum,
}: Props) {
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [searchInput, setSearchInput] = useState("");

  const isTablet = useSelector((state: RootState) => state.window.isTablet);

  const [searchParams] = useSearchParams();
  const history = useHistory();
  const isMount = useIsMount();

  useEffect(() => {
    if (isMount) {
      if (searchParams.has(SEARCH_FILTER_PARAM)) {
        setSearchFilter(searchParams.get(SEARCH_FILTER_PARAM)!.split(";"));
      }
      if (searchParams.has(USE_CASE_FILTER_PARAM)) {
        const requestTypes = searchParams.get(USE_CASE_FILTER_PARAM)!.split(";");
        setUseCaseFilter(requestTypes as RequestUseCase[]);
      }
      if (searchParams.has(PARTNERSHIP_MODELS_FILTER_PARAM)) {
        const partnershipModels = searchParams.get(PARTNERSHIP_MODELS_FILTER_PARAM)!.split(";");
        setPartnershipModelsFilter(partnershipModels as RfpPartnershipModel[]);
      }
      if (searchParams.has(PARTNER_TYPES_FILTER_PARAM)) {
        const partnerTypes = searchParams.get(PARTNER_TYPES_FILTER_PARAM)!.split(";");
        setPartnerTypesFilter(partnerTypes as RequestPartnerTypes[]);
      }
      return;
    }

    if (searchFilter.length) {
      searchParams.set(SEARCH_FILTER_PARAM, searchFilter.join(";"));
    } else {
      searchParams.delete(SEARCH_FILTER_PARAM);
    }
    if (useCaseFilter.length) {
      searchParams.set(USE_CASE_FILTER_PARAM, useCaseFilter.join(";"));
    } else {
      searchParams.delete(USE_CASE_FILTER_PARAM);
    }
    if (partnershipModelsFilter.length) {
      searchParams.set(PARTNERSHIP_MODELS_FILTER_PARAM, partnershipModelsFilter.join(";"));
    } else {
      searchParams.delete(PARTNERSHIP_MODELS_FILTER_PARAM);
    }
    if (partnerTypesFilter.length) {
      searchParams.set(PARTNER_TYPES_FILTER_PARAM, partnerTypesFilter.join(";"));
    } else {
      searchParams.delete(PARTNER_TYPES_FILTER_PARAM);
    }

    history.replace({ search: searchParams.toString() });
  }, [searchFilter, useCaseFilter, partnershipModelsFilter, partnerTypesFilter]);

  const handleClearFilters = () => {
    setSearchInput("");
    setSearchFilter([]);
    setUseCaseFilter([]);
    setPartnershipModelsFilter([]);
    setPartnerTypesFilter([]);
    setCurrentPageNum(1);
  };

  const searchFilterAsChips = searchFilter.map((value) => ({ value, label: value }));

  const searchBarEl = (
    <SearchBarInput
      value={searchInput}
      onChange={setSearchInput}
      onSubmit={(value) => {
        if (!value) return;
        setSearchFilter([...searchFilter, value]);
        setSearchInput("");
        setCurrentPageNum(1);
      }}
      placeholder="Expertise, request, etc."
      buttonText={null}
    />
  );

  const searchFilterEl =
    searchFilter.length > 0 ? (
      <div style={{ marginTop: 16 }}>
        <ChipsInput
          options={searchFilterAsChips}
          selectedValues={searchFilter}
          onChange={(values) => {
            setSearchFilter(values ?? []);
            setCurrentPageNum(1);
          }}
          gap="8px 8px"
          hideAll
        />
      </div>
    ) : null;

  const handleFilterChange = (target, setStateFunction) => (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setStateFunction((prev) => [...prev, target]);
    } else {
      setStateFunction((prev) => prev.filter((type) => type !== target));
    }
  };

  const commonFiltersEl = FEATURE_FLAGS.MARKETPLACE_PARTNER_TYPE_FILTER && (
    <>
      <FilterSection label="Companies Seeking" collapsible>
        <CheckboxContainer>
          {USE_CASE_OPTIONS.map((useCase) => (
            <Checkbox
              key={useCase}
              isReversed={isTablet}
              label={
                <CheckboxLabel withMargin={isTablet}>
                  {REQUEST_USE_CASE_LABELS[useCase]}
                </CheckboxLabel>
              }
              isChecked={useCaseFilter.includes(useCase)}
              onChange={handleFilterChange(useCase, setUseCaseFilter)}
              size="sm"
            />
          ))}
        </CheckboxContainer>
      </FilterSection>
      <FilterSection label="Partnership models" collapsible>
        <CheckboxContainer>
          {PARTNERSHIP_MODEL_OPTIONS.map((partnershipModel) => (
            <Checkbox
              key={partnershipModel}
              isReversed={isTablet}
              label={
                <CheckboxLabel withMargin={isTablet}>
                  {PARTNERSHIP_MODEL_LABELS[partnershipModel]}
                </CheckboxLabel>
              }
              isChecked={partnershipModelsFilter.includes(partnershipModel)}
              onChange={handleFilterChange(partnershipModel, setPartnershipModelsFilter)}
              size="sm"
            />
          ))}
        </CheckboxContainer>
      </FilterSection>
      <FilterSection label="Eligible partners" collapsible>
        <CheckboxContainer>
          {PARTNER_TYPE_OPTIONS.map((partnerType) => (
            <Checkbox
              key={partnerType}
              isReversed={isTablet}
              label={
                <CheckboxLabel withMargin={isTablet}>
                  {REQUEST_PARTNER_TYPE_LABELS[partnerType]}
                </CheckboxLabel>
              }
              isChecked={partnerTypesFilter.includes(partnerType)}
              onChange={handleFilterChange(partnerType, setPartnerTypesFilter)}
              size="sm"
            />
          ))}
        </CheckboxContainer>
      </FilterSection>
    </>
  );

  if (isTablet)
    return (
      <>
        <SlideModalBase
          isOpen={isPanelOpen}
          onClose={() => setIsPanelOpen(false)}
          closeOnDimmerClick
          direction="bottom"
          customCloseButton={
            <IconButton
              iconName="Close"
              size="sm"
              variant="fourth"
              onClick={() => setIsPanelOpen(false)}
              tooltipPosition="bottom"
              data-testid="close-button"
              margin="0 -6px 0 0"
            />
          }
        >
          <Panel>
            <FilterContainer>
              {commonFiltersEl}
              <FilterSection label="Sort by">
                <CheckboxContainer data-testid="sort-radio">
                  {SORT_BY_OPTIONS.map((option) => (
                    <Checkbox
                      key={`sort-${option.value}`}
                      isReversed={isTablet}
                      label={<CheckboxLabel withMargin={isTablet}>{option.label}</CheckboxLabel>}
                      isChecked={sortBy.value === option.value}
                      onChange={(e) => e.target.checked && setSortBy(option)}
                      size="sm"
                      type="radio"
                    />
                  ))}
                </CheckboxContainer>
              </FilterSection>
              <ResetFiltersButton onClick={handleClearFilters} />
            </FilterContainer>
            <PanelFooter>
              <Button variant="ghost" onClick={() => setIsPanelOpen(false)}>
                Cancel
              </Button>
              <Button onClick={() => setIsPanelOpen(false)}>
                View {totalCount} partnering requests
              </Button>
            </PanelFooter>
          </Panel>
        </SlideModalBase>
        <div style={{ marginBottom: 22 }}>
          <SearchAndFilter>
            <div style={{ width: "100%" }}>{searchBarEl}</div>
            <IconButton
              iconName="Filter & sort"
              variant="fourth"
              tooltipWidth="90px"
              onClick={() => setIsPanelOpen(true)}
            />
          </SearchAndFilter>
          {searchFilterEl}
        </div>
      </>
    );

  return (
    <FilterContainer>
      <FilterSection label="Search" labelMargin="0 0 23px">
        {searchBarEl}
        {searchFilterEl}
      </FilterSection>
      {commonFiltersEl}
      <ResetFiltersButton onClick={handleClearFilters} />
    </FilterContainer>
  );
}

const SearchAndFilter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 16px;
`;

const Panel = styled.div`
  height: calc(100vh - 80px);
  width: 100%;

  ${FilterContainer} {
    padding: 45px 24px;
    overflow-y: auto;
    max-height: calc(100vh - 84px);
  }
`;

const PanelFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px;
  border-top: 1px solid ${COLORS.NEUTRAL_250};
`;

const CheckboxLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  ${({ withMargin }) =>
    withMargin &&
    `
    margin: 4px;
  `}
`;
